import React from 'react'
import { usePage } from '@inertiajs/react'
import GuideLayout from '@/components/help/GuideLayout'
import GuideSection from '@/components/help/GuideSection'
import GuideWidget from '@/components/help/GuideWidget'
import GuideTip from '@/components/help/GuideTip'
import GuideRelatedLinks from '@/components/help/GuideRelatedLinks'
import LeadTimeCurveBuilder from '@/components/help/widgets/LeadTimeCurveBuilder'
import type { SharedProps } from '@/types/shared'

export default function LeadTime({ currentUser, properties }: Pick<SharedProps, 'currentUser' | 'properties'>) {
  // Room-Type Demand is globally kill-switched (App::RateEngine::Config); drop it from the list
  // of demand stages that lead time amplifies when it is off.
  const roomDemandEnabled = usePage().props.roomDemandEnabled === true
  return (
    <GuideLayout
      currentUser={currentUser}
      properties={properties}
      title="Lead Time Amplification"
      subtitle="How the booking window shapes demand sensitivity — closer dates react more strongly."
    >
      <GuideSection title="Why Lead Time Matters">
        <p className="text-sm leading-relaxed text-muted-foreground">
          A room booked 6 months out has different economics than one booked tomorrow. Being 50%
          booked for a date 2 months away is fine — there is plenty of time to sell remaining rooms.
          Being 50% booked for tomorrow is a problem that needs an immediate response.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Lead time amplification is{' '}
          <strong className="text-foreground">built into the demand stages</strong> (Property Demand
          {roomDemandEnabled ? ' and Room Demand' : ''}). It controls how strongly the engine reacts to booking pace signals based
          on how far away the stay date is. This is not a separate pipeline stage — it is an{' '}
          <strong className="text-foreground">amplifier</strong> that makes demand adjustments more
          or less aggressive depending on the booking window.
        </p>
      </GuideSection>

      <GuideSection title="How It Works">
        <p className="text-sm leading-relaxed text-muted-foreground">
          The lead time curve defines an{' '}
          <strong className="text-foreground">amplification factor</strong> at each point in the
          booking window. The demand adjustment is multiplied by this factor before being applied:
        </p>
        <div className="rounded-lg border bg-muted/50 p-4 space-y-1">
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Final demand adjustment</strong> = base demand % ×
            lead time amplifier
          </p>
        </div>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Far out (60+ days)</strong> — amplifier is below 1.0.
            Demand signals are dampened because there is plenty of time for bookings to arrive
            naturally.
          </li>
          <li>
            <strong className="text-foreground">Medium range (14–60 days)</strong> — amplifier is
            around 1.0. The engine reacts at its configured sensitivity level.
          </li>
          <li>
            <strong className="text-foreground">Close in (1–14 days)</strong> — amplifier is above
            1.0. The engine reacts more strongly because there is less time to fill remaining rooms.
          </li>
        </ul>
        <div className="rounded-lg border bg-muted/50 p-4">
          <p className="text-sm text-muted-foreground">
            <strong className="text-foreground">Example:</strong> If balanced sensitivity says +12%
            for strong demand, that becomes roughly +6% at 60 days out (stay calm) but +19% at 3
            days out (act now). The same demand signal, different urgency.
          </p>
        </div>

        <GuideWidget
          title="Lead Time Curve"
          footnote="See how the amplifier changes across the booking window. The actual effect depends on your demand sensitivity setting."
        >
          <LeadTimeCurveBuilder />
        </GuideWidget>
      </GuideSection>

      <GuideSection title="Why Not a Separate Stage?">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Lead time is not a separate percentage on top of demand — it{' '}
          <strong className="text-foreground">shapes how demand responds</strong>. This avoids the
          problem of two independent adjustments compounding unpredictably. Instead, lead time and
          demand work as a single, coherent signal: "how far off-pace is this date, and how urgently
          should we respond?"
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          This also means your <strong className="text-foreground">guardrails</strong> (floor and
          ceiling) and <strong className="text-foreground">demand cap</strong> naturally limit the
          combined effect. You do not need to worry about lead time and demand pushing rates beyond
          your boundaries.
        </p>
      </GuideSection>

      <GuideSection title="Configuring Lead Time">
        <p className="text-sm leading-relaxed text-muted-foreground">
          The lead time curve is configured in the{' '}
          <strong className="text-foreground">Advanced</strong> section of the Demand Response
          settings page. Most properties never need to change the defaults — the built-in curve
          already dampens far-out signals and amplifies close-in ones.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          If you do adjust it, the key principle is simple: the closer the stay date, the higher the
          amplifier should be. A flat curve (all 1.0) means lead time has no effect — the engine
          reacts the same way regardless of when check-in is.
        </p>
      </GuideSection>

      <GuideSection title="Tips">
        <div className="space-y-3">
          <GuideTip type="do">
            Start with the defaults. The built-in lead time curve already handles most properties
            well — dampening far-out noise and amplifying urgent close-in signals.
          </GuideTip>
          <GuideTip type="do">
            If you notice the engine reacting too aggressively to last-minute demand changes,
            consider lowering the close-in amplifier rather than reducing your overall sensitivity.
          </GuideTip>
          <GuideTip type="dont">
            Don't set the far-out amplifier too high. Reacting strongly to demand signals 90+ days
            out often just creates noise — those dates have plenty of time to fill naturally.
          </GuideTip>
        </div>
      </GuideSection>

      <GuideRelatedLinks
        links={[
          { slug: 'demand-response', title: 'Demand Response' },
          { slug: 'price-guardrails', title: 'Price Guardrails' },
          { slug: 'how-it-works', title: 'How It Works' },
        ]}
      />
    </GuideLayout>
  )
}
